// Generated from owned source modules. Do not edit; run build_runtime.py.
const wishlist_protocol = (() => {
// Public pure state protocol. No credentials, provider clients or DOM access.
class WishlistError extends Error {
  constructor(code, state = null) {
    super(code);
    this.name = 'WishlistError';
    this.code = code;
    this.state = state;
  }
}
function emptyState() {
  return { revision: 0, items: {}, operations: {} };
}
function snapshot(state) {
  return { revision: state.revision, items: structuredClone(state.items) };
}
function validateOperation(op) {
  const allowed = new Set(['operation_id', 'product_gid', 'present', 'preferred_variant_gid', 'expected_item_version']);
  if (!op || Object.keys(op).some((k) => !allowed.has(k)) ||
      !/^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(op.operation_id || '') ||
      !/^gid:\/\/shopify\/Product\/[1-9][0-9]*$/.test(op.product_gid || '') ||
      typeof op.present !== 'boolean' ||
      !Number.isSafeInteger(op.expected_item_version) || op.expected_item_version < 0 ||
      (op.preferred_variant_gid !== null &&
       !/^gid:\/\/shopify\/ProductVariant\/[1-9][0-9]*$/.test(op.preferred_variant_gid || ''))) {
    throw new WishlistError('INVALID_OPERATION');
  }
}
function validateSnapshot(state) {
  if (!state || !Number.isSafeInteger(state.revision) || state.revision < 0 ||
      Object.keys(state).some((key) => !['revision', 'items'].includes(key)) ||
      !state.items || Array.isArray(state.items) || typeof state.items !== 'object') {
    throw new WishlistError('INVALID_STATE');
  }
  for (const [key, item] of Object.entries(state.items)) {
    if (!item || Object.keys(item).some((field) => !['product_gid', 'present', 'preferred_variant_gid', 'item_version', 'saved_revision'].includes(field)) ||
        item.product_gid !== key || !/^gid:\/\/shopify\/Product\/[1-9][0-9]*$/.test(key) ||
        typeof item.present !== 'boolean' || !Number.isSafeInteger(item.item_version) || item.item_version < 0 ||
        !Number.isSafeInteger(item.saved_revision) || item.saved_revision < 0 || item.saved_revision > state.revision ||
        (item.preferred_variant_gid !== null &&
         !/^gid:\/\/shopify\/ProductVariant\/[1-9][0-9]*$/.test(item.preferred_variant_gid || ''))) {
      throw new WishlistError('INVALID_STATE');
    }
  }
  return state;
}
function applyOperation(state, op, { maxItems = 200, eligible = () => true } = {}) {
  validateOperation(op);
  const digest = JSON.stringify([op.product_gid, op.present, op.preferred_variant_gid, op.expected_item_version]);
  const previous = state.operations[op.operation_id];
  if (previous) {
    if (previous.digest !== digest) throw new WishlistError('ID_REUSED');
    return structuredClone(previous.result);
  }
  const old = state.items[op.product_gid];
  const version = old?.item_version || 0;
  if (version !== op.expected_item_version) throw new WishlistError('CONFLICT', snapshot(state));
  if (op.present && !eligible(op.product_gid, op.preferred_variant_gid)) throw new WishlistError('INELIGIBLE');
  if (op.present && !old?.present &&
      Object.values(state.items).filter((item) => item.present).length >= maxItems) {
    throw new WishlistError('LIMIT');
  }
  const changed = Boolean(old?.present) !== op.present ||
    (op.present && old?.preferred_variant_gid !== op.preferred_variant_gid);
  if (changed) {
    state.revision += 1;
    state.items[op.product_gid] = {
      product_gid: op.product_gid,
      present: op.present,
      preferred_variant_gid: op.present ? op.preferred_variant_gid : old?.preferred_variant_gid || null,
      item_version: version + 1,
      saved_revision: op.present ? state.revision : old?.saved_revision || 0
    };
  }
  const result = {
    operation_id: op.operation_id,
    status: changed ? 'applied' : 'no_change',
    revision: state.revision,
    item: structuredClone(state.items[op.product_gid] || {
      product_gid: op.product_gid, present: false, preferred_variant_gid: null,
      item_version: 0, saved_revision: 0
    })
  };
  state.operations[op.operation_id] = { digest, result: structuredClone(result) };
  return result;
}

return { WishlistError, emptyState, snapshot, validateOperation, validateSnapshot, applyOperation };
})();
const wishlist_journal = (() => {
const {  emptyState, snapshot, validateSnapshot, validateOperation, WishlistError  } = wishlist_protocol;

function validateRecord(record) {
  if (record?.schema !== 1 || !/^[a-f0-9]{64}$/.test(record.credential || '') ||
      !Array.isArray(record.queue) || record.queue.length > 1000) throw new WishlistError('INVALID_STATE');
  validateSnapshot(record.state);
  for (const item of record.queue) {
    if (!item) throw new WishlistError('INVALID_STATE');
    const { created_at, sent, ...operation } = item;
    validateOperation(operation);
    if (!Number.isSafeInteger(created_at) || created_at < 0 || typeof sent !== 'boolean') throw new WishlistError('INVALID_STATE');
  }
  return record;
}

// Native in-memory transaction fixture. A persistent production adapter must be explicit.
class MemoryJournal {
  constructor(record = null, persistence = null) {
    this.record = record && structuredClone(record);
    this.persistence = persistence;
    this.serial = Promise.resolve();
    this.listeners = new Set();
  }
  get available() { return this.persistence ? this.persistence.available : true; }
  subscribe(callback) { this.listeners.add(callback); return () => this.listeners.delete(callback); }
  transaction(change) {
    const work = this.serial.then(() => {
      const before = this.record && JSON.stringify(this.record);
      const draft = this.record && structuredClone(this.record);
      const result = change(draft);
      const next = result.record ?? draft;
      validateRecord(next);
      this.record = next;
      if (JSON.stringify(next) !== before) {
        this.persistence?.write(JSON.stringify(next));
        for (const callback of this.listeners) callback(structuredClone(next));
      }
      return { record: structuredClone(next), value: structuredClone(result.value) };
    });
    this.serial = work.catch(() => {});
    return work;
  }
  read() { return this.transaction((record) => ({ record, value: null })); }
  close() { this.listeners.clear(); }
}

class IndexedDBJournal {
  constructor({ databaseName = 'myart-wishlist-v1-qa-journal-v1', recordKey = 'guest', ephemeralState = null } = {}) {
    if (recordKey !== 'guest' && !/^account:[a-f0-9-]{36}$/.test(recordKey)) throw new WishlistError('INVALID_STATE');
    if ((recordKey === 'guest') !== (ephemeralState === null)) throw new WishlistError('INVALID_STATE');
    if (ephemeralState !== null) validateSnapshot(ephemeralState);
    this.databaseName = databaseName;
    this.recordKey = recordKey;
    this.ephemeralState = ephemeralState === null ? null : structuredClone(ephemeralState);
    this.listeners = new Set();
    this.usable = true;
    this.closed = false;
    this.generation = 0;
    this.database = null;
    this.ready = null;
    this.opened = false;
    this.fallbackOnly = false;
    this.retryAfter = 0;
    this.fallback = new MemoryJournal();
    this.fallback.subscribe(record => this.notify(record));
    this.channel = typeof BroadcastChannel === 'function' ? new BroadcastChannel(databaseName) : null;
    this.channel?.addEventListener('message', () => {
      if (!this.closed) void this.read().then(({ record }) => this.notify(record)).catch(() => {});
    });
    void this.connection().catch(() => {});
  }
  get available() { return !this.closed && this.usable; }
  subscribe(callback) { this.listeners.add(callback); return () => this.listeners.delete(callback); }
  notify(record) { if (!this.closed) for (const callback of this.listeners) callback(structuredClone(record)); }
  invalidate(database) {
    if (this.database !== database) return;
    this.database = null;
    this.usable = false;
    database.close();
  }
  connection() {
    if (this.closed) return Promise.reject(new WishlistError('LOCAL_STORAGE'));
    if (this.fallbackOnly) return Promise.resolve(null);
    if (this.database) return Promise.resolve(this.database);
    if (this.ready) return this.ready;
    if (Date.now() < this.retryAfter) return Promise.reject(new WishlistError('LOCAL_STORAGE'));
    const generation = this.generation;
    const attempt = new Promise((resolve, reject) => {
      let settled = false;
      const fail = () => { if (!settled) { settled = true; reject(new WishlistError('LOCAL_STORAGE')); } };
      try {
        const request = indexedDB.open(this.databaseName, 1);
        request.onupgradeneeded = () => {
          if (settled || this.closed || generation !== this.generation) { request.transaction?.abort(); return; }
          if (!request.result.objectStoreNames?.contains('state')) request.result.createObjectStore('state');
        };
        request.onsuccess = () => {
          const database = request.result;
          if (settled || this.closed || generation !== this.generation) { database.close(); fail(); return; }
          settled = true;
          this.database = database;
          this.opened = true;
          this.usable = true;
          database.onclose = () => this.invalidate(database);
          database.onversionchange = () => this.invalidate(database);
          resolve(database);
        };
        request.onerror = fail;
        request.onblocked = fail;
      } catch { fail(); }
    });
    const pending = attempt.catch(error => {
      this.usable = false;
      // Only an initial open failure can enter session-only mode. After durable
      // use, never replace the persisted queue/credential with an empty record.
      if (!this.opened && !this.closed) { this.fallbackOnly = true; return null; }
      this.retryAfter = Date.now() + 1000;
      throw error;
    }).finally(() => { if (this.ready === pending) this.ready = null; });
    this.ready = pending;
    return pending;
  }
  async transaction(change) {
    let database = await this.connection();
    if (this.closed) throw new WishlistError('LOCAL_STORAGE');
    if (!database) return this.fallback.transaction(change);
    let tx;
    // InvalidStateError here proves no transaction, callback or write started.
    // Reopen once and reread the committed record before applying the intent.
    for (let attempt = 0; attempt < 2; attempt += 1) {
      try { tx = database.transaction('state', 'readwrite'); break; }
      catch (error) {
        this.usable = false;
        if (attempt || error?.name !== 'InvalidStateError') throw new WishlistError('LOCAL_STORAGE');
        this.invalidate(database);
        database = await this.connection();
        if (!database || this.closed) throw new WishlistError('LOCAL_STORAGE');
      }
    }
    return new Promise((resolve, reject) => {
      const table = tx.objectStore('state');
      const request = table.get(this.recordKey);
      let result, failure, changed = false;
      request.onsuccess = () => {
        try {
          const original = request.result || null;
          const draft = original && structuredClone(original);
          if (draft && this.ephemeralState !== null) draft.state = structuredClone(this.ephemeralState);
          const mutation = change(draft);
          const record = mutation.record ?? draft;
          validateRecord(record);
          const stored = this.ephemeralState === null ? record : {
            schema: record.schema, credential: record.credential,
            state: snapshot(emptyState()), queue: record.queue,
            problem: record.problem || null,
            catalog_hints: Object.fromEntries(Object.entries(record.catalog_hints || {}).filter(([id]) =>
              record.queue.some(operation => operation.product_gid === id)))
          };
          changed = JSON.stringify(stored) !== JSON.stringify(original);
          result = { record: structuredClone(record), value: structuredClone(mutation.value) };
          if (changed) table.put(stored, this.recordKey);
        } catch (error) { failure = error; tx.abort(); }
      };
      tx.oncomplete = () => {
        if (!this.closed) {
          this.usable = true;
          if (this.ephemeralState !== null) this.ephemeralState = structuredClone(result.record.state);
          if (changed) { this.notify(result.record); this.channel?.postMessage({ changed: true }); }
        }
        resolve(result);
      };
      // An abort is not retried inside the journal: the caller reconciles the
      // durable record/operation IDs. Never replay an uncertain logical write.
      tx.onabort = () => { this.usable = false; reject(failure || new WishlistError('LOCAL_STORAGE')); };
      tx.onerror = () => { failure ||= new WishlistError('LOCAL_STORAGE'); };
    });
  }
  read() { return this.transaction(record => ({ record, value: null })); }
  async close() {
    if (this.closed) return;
    this.closed = true;
    this.generation += 1;
    this.usable = false;
    this.channel?.close();
    this.listeners.clear();
    this.fallback.close();
    this.database?.close();
    this.database = null;
    (await this.ready?.catch(() => null))?.close();
  }
}

return { validateRecord, MemoryJournal, IndexedDBJournal };
})();
const wishlist_store = (() => {
const { MemoryJournal, validateRecord } = wishlist_journal;

const { emptyState, snapshot, validateOperation, validateSnapshot, WishlistError } = wishlist_protocol;



function randomHex(bytes = 32) {

  return Array.from(crypto.getRandomValues(new Uint8Array(bytes)), (b) => b.toString(16).padStart(2, '0')).join('');

}

function operationId() {

  const hex = randomHex(16);

  return hex.slice(0, 8) + '-' + hex.slice(8, 12) + '-4' + hex.slice(13, 16) +

    '-a' + hex.slice(17, 20) + '-' + hex.slice(20);

}

function browserPersistence(storage, key) {

  let available = true;

  return {

    key,

    get available() { return available; },

    read() {

      try { return storage.getItem(key); }

      catch { available = false; return null; }

    },

    write(value) {

      try { storage.setItem(key, value); }

      catch { available = false; }

    }

  };

}

function readCache(persistence) {

  try {

    const data = JSON.parse(persistence.read());

    if (data?.schema !== 1 || !/^[0-9a-f]{64}$/.test(data.credential) ||

        !Array.isArray(data.queue) || data.queue.length > 1000) return null;

    validateSnapshot(data.state);

    for (const item of data.queue) {

      const { created_at, sent, ...op } = item;

      validateOperation(op);

      if (!Number.isSafeInteger(created_at) || created_at < 0 || typeof sent !== 'boolean') return null;

    }

    return data;

  } catch { return null; }

}

class GuestWishlistStore {

  constructor({ transport, persistence, journal = null, catalogHint = () => null, now = Date.now }) {

    this.transport = transport;

    this.persistence = persistence;

    this.now = now;

    this.catalogHint = catalogHint;

    const cached = readCache(persistence);

    this.credential = cached?.credential || randomHex();

    this.base = cached?.state || snapshot(emptyState());

    this.queue = cached?.queue || [];

    this.listeners = new Set();

    this.syncStatus = this.queue.length ? 'pending' : 'loading';

    this.problem = null;

    this.running = null;

    this.destroyed = false;

    this.generation = 0;

    this.localIntents = [];

    this.pendingWrites = new Set();

    this.journal = journal || new MemoryJournal(cached && { ...cached, problem: null }, persistence);

    this.unsubscribeJournal = this.journal.subscribe((record) => {

      if (!this.destroyed) { this.adopt(record); void this.flush(); }

    });

    this.ready = this.journal.transaction((record) => ({ record: record || {

      schema: 1, credential: this.credential, state: this.base, queue: this.queue, problem: null

    }, value: null })).then(({ record }) => { if (!this.destroyed) this.adopt(record); return record; });

  }

  adopt(record) {

    validateRecord(record);

    this.credential = record.credential;

    this.base = structuredClone(record.state);

    this.queue = structuredClone(record.queue);

    this.catalogHints = Object.fromEntries(Object.entries(record.catalog_hints || {}).filter(([id,handle]) =>
      /^gid:\/\/shopify\/Product\/[1-9][0-9]*$/.test(id) && typeof handle === 'string' && /^[a-z0-9][a-z0-9_-]{0,200}$/.test(handle)));

    this.problem = record.problem || null;

    this.syncStatus = this.queue.length ? 'pending' : this.problem ? 'review' : 'synced';

    // Compatibility cache is a backup seed only; the journal is authoritative.

    this.persistence.write(JSON.stringify(record));

    this.emit();

  }

  get view() {

    const items = structuredClone(this.base.items);

    // Only the journal queue is durable. A different transaction can notify
    // subscribers while a newer click is still waiting to commit.
    for (const intent of this.queue) {

      items[intent.product_gid] = { ...items[intent.product_gid], product_gid: intent.product_gid,

        present: intent.present, preferred_variant_gid: intent.preferred_variant_gid, pending: true };

    }

    return { items, count: Object.values(items).filter((item) => item.present).length,

      revision: this.base.revision, syncStatus: this.syncStatus, problem: this.problem,

      pendingCount: this.queue.length + this.localIntents.length, persistenceAvailable: this.journal.available };

  }

  subscribe(listener) { this.listeners.add(listener); listener(this.view); return () => this.listeners.delete(listener); }

  emit() { if (!this.destroyed) for (const listener of this.listeners) listener(this.view); }

  setItem(productId, present, preferredVariantId = null) {

    if (this.destroyed) return;

    const intent = { product_gid: productId, present, preferred_variant_gid: preferredVariantId,

      operation_id: operationId(), expected_item_version: this.base.items[productId]?.item_version || 0,

      created_at: this.now(), sent: false };

    this.localIntents.push(intent);

    this.syncStatus = 'pending';

    // The saved count is painted after the local transaction commits. A network

    // reconnect may dispose this UI, but must not discard an accepted click.

    const write=(async()=>{

    try {

      const initial=await this.ready;

      const { record } = await this.journal.transaction((draft) => {

        if (draft.credential!==initial.credential) return { record: draft, value: null };

        const handle=this.catalogHint(productId);

        if (/^[a-z0-9][a-z0-9_-]{0,200}$/.test(handle || '')) {

          draft.catalog_hints ||= {};

          // Keep only referenced public handles; no customer/profile data.

          const retained=new Set([...Object.values(draft.state.items).filter((item)=>item.present).map((item)=>item.product_gid),...draft.queue.map((item)=>item.product_gid),productId]);

          draft.catalog_hints=Object.fromEntries(Object.entries(draft.catalog_hints).filter(([id])=>retained.has(id)));

          draft.catalog_hints[productId]=handle;

        }

        const previous = [...draft.queue].reverse().find((item) => item.product_gid === productId && !item.sent);

        // A preceding acknowledgement can commit between the click and this
        // transaction (notably Remove -> Undo). Author the new intention from
        // the current durable version; never rebase an already sent operation.
        intent.expected_item_version = draft.state.items[productId]?.item_version || 0;

        if (previous) {

          previous.present = present;

          previous.preferred_variant_gid = preferredVariantId;

          previous.expected_item_version = intent.expected_item_version;

        } else draft.queue.push(intent);

        draft.problem = null;

        return { record: draft, value: null };

      });

      if (this.destroyed) return;

      this.localIntents = this.localIntents.filter((item) => item !== intent);

      this.adopt(record);

      void this.flush();

    } catch {

      this.problem = 'LOCAL_STORAGE';

      this.syncStatus = 'pending';

      this.emit();

    }

    })();

    this.pendingWrites.add(write);

    void write.finally(()=>this.pendingWrites.delete(write));

    return write;

  }

  async refresh() {

    if (this.destroyed) return;

    const generation = this.generation;

    try {

      await this.ready;

      const credential = this.credential;

      const state = validateSnapshot(await this.transport.state(credential));

      if (this.destroyed || generation !== this.generation) return;

      const { record } = await this.journal.transaction((draft) => {

        if (this.destroyed || generation !== this.generation || draft.credential !== credential) return { record: draft, value: null };

        if (state.revision >= draft.state.revision) draft.state = state;

        if (draft.problem === 'NETWORK') draft.problem = null;

        return { record: draft, value: null };

      });

      if (this.destroyed || generation !== this.generation) return;

      this.adopt(record);

      await this.flush();

    } catch {

      if (!this.destroyed) {

        this.problem = 'NETWORK';

        this.syncStatus = this.queue.length || this.localIntents.length ? 'pending' : 'unavailable';

        this.emit();

      }

    }

  }

  flush() {

    if (this.running || this.destroyed) return this.running || Promise.resolve();

    this.running = this.pump().catch(() => {

      if (!this.destroyed) { this.problem = 'LOCAL_STORAGE'; this.syncStatus = 'unavailable'; this.emit(); }

    }).finally(() => {

      this.running = null;

      if (!this.destroyed && this.queue.length && !['NETWORK', 'LOCAL_STORAGE'].includes(this.problem)) void this.flush();

    });

    return this.running;

  }

  async pump() {

    await this.ready;

    const generation = this.generation;

    while (!this.destroyed) {

      const { record, value: first } = await this.journal.transaction((draft) => {

        if (this.destroyed || generation !== this.generation) return { record: draft, value: null };

        while (draft.queue[0] && !draft.queue[0].sent && this.now() - draft.queue[0].created_at > 7*86400000) {

          draft.queue.shift(); draft.problem = 'EXPIRED';

        }

        if (draft.queue[0]) draft.queue[0].sent = true;

        return { record: draft, value: draft.queue[0] || null };

      });

      if (this.destroyed || generation !== this.generation) return;

      this.adopt(record);

      if (!first || this.destroyed) break;

      const credential = record.credential;

      const { created_at, sent, ...operation } = first;

      try {

        const result = await this.transport.set(credential, operation, this.catalogHints[first.product_gid] || null);

        if (this.destroyed || generation !== this.generation) return;

        validateSnapshot({ revision: result.revision, items: { [first.product_gid]: result.item } });

        if (result.operation_id !== first.operation_id) throw new WishlistError('INVALID_STATE');

        const updated = await this.journal.transaction((draft) => {

          if (this.destroyed || generation !== this.generation || draft.credential !== credential) return { record: draft, value: null };

          const index = draft.queue.findIndex((item) => item.operation_id === first.operation_id);

          if (index < 0) return { record: draft, value: null }; // Other tab already acknowledged exactly this UUID.

          const current = draft.state.items[first.product_gid]?.item_version || 0;

          if (result.item.item_version >= current) draft.state.items[first.product_gid] = result.item;

          draft.state.revision = Math.max(draft.state.revision, result.revision);

          draft.queue.splice(index, 1);

          if (current <= result.item.item_version) for (const item of draft.queue) {

            if (!item.sent && item.product_gid === first.product_gid && item.expected_item_version === first.expected_item_version) {

              item.expected_item_version = result.item.item_version;

            }

          }

          if (draft.problem === 'NETWORK') draft.problem = null;

          return { record: draft, value: null };

        });

        if (this.destroyed || generation !== this.generation) return;

        this.adopt(updated.record);

      } catch (error) {

        if (this.destroyed || generation !== this.generation) return;

        const rejected = error instanceof WishlistError &&

          ['CONFLICT','INELIGIBLE','LIMIT','INVALID_OPERATION','ID_REUSED'].includes(error.code);

        const updated = await this.journal.transaction((draft) => {

          if (this.destroyed || generation !== this.generation || draft.credential !== credential) return { record: draft, value: null };

          const index = draft.queue.findIndex((item) => item.operation_id === first.operation_id);

          if (index < 0) return { record: draft, value: null };

          if (rejected) {

            if (error.state && error.state.revision >= draft.state.revision) draft.state = validateSnapshot(error.state);

            draft.queue.splice(index, 1);

          }

          draft.problem = rejected ? error.code : 'NETWORK';

          return { record: draft, value: null };

        });

        this.adopt(updated.record);

        if (!rejected) break;

      }

    }

  }

  destroy() {

    this.destroyed = true; this.generation += 1;

    this.unsubscribeJournal(); this.listeners.clear();

    return Promise.allSettled([...this.pendingWrites]);

  }

}




return { randomHex, operationId, browserPersistence, GuestWishlistStore };
})();
const wishlist_ui = (() => {
const noticeControllers = new WeakMap();
function wishlistNotice(root) {
  if (noticeControllers.has(root)) return noticeControllers.get(root);
  const doc = root.ownerDocument, win = doc.defaultView;
  const notice = root.querySelector('[data-wishlist-notice]');
  const undo = root.querySelector('[data-wishlist-undo]');
  const status = root.querySelector('[data-wishlist-status]');
  const label = notice.querySelector('span'), defaultLabel = label.textContent;
  let timer = null, announcementTimer = null, deadline = 0, expire = null;
  const api = {
    show(message = defaultLabel, onExpire = null) {
      win.clearTimeout(timer); expire = onExpire; deadline = Date.now() + 5000;
      label.textContent = message; notice.hidden = false;
      timer = win.setTimeout(() => api.dismiss(), 5000);
    },
    announce(message) {
      win.clearTimeout(announcementTimer);
      if (status.textContent !== message) status.textContent = message;
      announcementTimer = win.setTimeout(() => { status.textContent = ''; }, 5000);
    },
    dismiss() {
      win.clearTimeout(timer); timer = null; deadline = 0;
      notice.hidden = true; undo.hidden = true; label.textContent = defaultLabel;
      const callback = expire; expire = null; callback?.();
    },
    destroy() {
      api.dismiss(); win.clearTimeout(announcementTimer); status.textContent = '';
      doc.removeEventListener('visibilitychange', visibility);
      noticeControllers.delete(root);
    }
  };
  const visibility = () => { if (deadline && Date.now() >= deadline) api.dismiss(); };
  doc.addEventListener('visibilitychange', visibility);
  noticeControllers.set(root, api); return api;
}

const { GuestWishlistStore } = wishlist_store;
const instances = new WeakMap();
const ownControl = '[data-myart-wishlist-product],[data-wishlist-open],[data-wishlist-close],[data-wishlist-remove],[data-wishlist-undo],[data-wishlist-retry],[data-wishlist-review-add],[data-wishlist-review-dismiss],[data-wishlist-card-link]';
const navigationKeys = new Set(['Tab', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Enter', ' ']);

function safeUrl(value, document, image = false) {
  if (typeof value !== 'string' || !value.trim()) return null;
  try {
    const url = new URL(value, document.baseURI);
    if (url.origin === new URL(document.baseURI).origin && ['http:', 'https:'].includes(url.protocol)) return url.href;
    if (image && url.protocol === 'https:' && url.hostname === 'cdn.shopify.com') return url.href;
  } catch { /* Safe empty media/link fallback. */ }
  return null;
}
function heartIcon(document) {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('aria-hidden', 'true');
  const path = document.createElementNS(svg.namespaceURI, 'path');
  path.setAttribute('d', 'M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1.1-1.1a5.5 5.5 0 0 0-7.8 7.8L12 21l8.8-8.6a5.5 5.5 0 0 0 0-7.8Z');
  svg.append(path);
  return svg;
}
function renderRating(container, product, document) {
  const stars=document.createElement('span');stars.className='myart-wishlist-row__stars';
  stars.setAttribute('role','img');stars.setAttribute('aria-label',product.ratingLabel);
  const paths=["m16.399 14.574 4.413-4.283-6.086-.894L12 3.887l-2.726 5.51-6.086.894L7.6 14.574l-1.053 6.072L12 17.776l5.438 2.87-1.039-6.072ZM24 9.425c0 .212-.125.443-.375.693l-5.236 5.105 1.24 7.212c.01.067.015.164.015.289 0 .48-.197.72-.591.72-.183 0-.375-.057-.577-.172L12 19.867l-6.476 3.404c-.212.115-.404.173-.577.173-.202 0-.353-.07-.454-.21a.85.85 0 0 1-.152-.511c0-.058.01-.154.03-.289l1.24-7.211-5.25-5.106C.12 9.858 0 9.628 0 9.425c0-.355.27-.577.808-.663l7.24-1.053 3.245-6.562c.183-.395.418-.592.707-.592s.524.197.707.592l3.245 6.562 7.24 1.053c.539.086.808.308.808.663Z", "M24 9.425c0 .212-.125.443-.375.693l-5.236 5.105 1.24 7.212c.01.067.015.164.015.289a.85.85 0 0 1-.151.511.51.51 0 0 1-.44.21c-.183 0-.375-.058-.577-.174L12 19.869l-6.476 3.404c-.212.115-.404.173-.577.173-.202 0-.353-.07-.454-.21a.85.85 0 0 1-.152-.511c0-.058.01-.154.03-.289l1.24-7.211-5.25-5.106C.12 9.858 0 9.628 0 9.425c0-.355.27-.577.808-.663l7.24-1.053 3.245-6.562c.183-.395.418-.592.707-.592s.524.197.707.592l3.245 6.562 7.24 1.053c.539.086.808.308.808.663Z"];
  for(let i=0;i<5;i++){
    const svg=document.createElementNS('http://www.w3.org/2000/svg','svg');
    svg.setAttribute('viewBox','0 0 24 24');svg.setAttribute('aria-hidden','true');
    const fill=Math.max(0,Math.min(100,(Math.ceil(product.rating*2)-i*2)*50));
    paths.forEach((d,index)=>{const path=document.createElementNS(svg.namespaceURI,'path');path.setAttribute('d',d);
      path.style.clipPath=index?`inset(0 ${100-fill}% 0 0)`:`inset(0 0 0 ${fill}%)`;svg.append(path);});
    stars.append(svg);
  }
  const count=document.createElement('span');count.textContent=' ('+product.reviewCount+')';count.setAttribute('aria-hidden','true');
  const label=document.createElement('span');label.className='myart-wishlist-row__sr';label.textContent=product.reviewCount+' '+product.reviewLabel;
  container.append(stars,count,label);
}
function wishlistSyncText(view, mode, strings) {
  const problems = {
    NETWORK: strings.pending, CONFLICT: strings.conflict, EXPIRED: strings.expired,
    INELIGIBLE: strings.unavailable, LIMIT: strings.limit, INVALID_OPERATION: strings.error,
    ID_REUSED: strings.error
  };
  return (!view.persistenceAvailable && (mode === 'guest' || view.pendingCount > 0)) || view.problem === 'LOCAL_STORAGE' ? strings.sessionOnly :
    view.pendingCount ? strings.pending : view.problem ? problems[view.problem] || strings.error :
      view.syncStatus === 'loading' ? strings.loading : mode === 'account' ? strings.savedAccount : strings.savedBrowser;
}
function bootWishlist(root, { transport, persistence, journal = null, catalog, mode = 'guest', ingestButtons = () => {}, overlayHooks = {}, catalogHint = () => null, retryIdentity = null }) {
  const document = root.ownerDocument;
  const existing = instances.get(document);
  if (existing && existing.root.isConnected) return existing;
  const controller = new AbortController();
  const { signal } = controller;
  const store = new GuestWishlistStore({ transport, persistence, journal, catalogHint });
  const dialog = root.querySelector('[data-wishlist-dialog]');
  const isPage = root.hasAttribute('data-wishlist-page');
  const pageUrl = safeUrl(root.dataset.wishlistUrl, document);
  const focusTarget = () => root.querySelector(isPage ? (root.dataset.wishlistPresentation === 'populated' ? '[data-wishlist-page-title]' : '[data-wishlist-empty-title]') : '[data-wishlist-close]');
  const list = root.querySelector('[data-wishlist-list]');
  const empty = root.querySelector('[data-wishlist-empty]');
  const count = root.querySelector('[data-wishlist-count]');
  const sync = root.querySelector('[data-wishlist-sync]');
  const notice = root.querySelector('[data-wishlist-notice]');
  const panelNotice = root.querySelector('[data-wishlist-panel-notice]');
  const undo = root.querySelector('[data-wishlist-undo]');
  const status = root.querySelector('[data-wishlist-status]');
  const strings = JSON.parse(root.querySelector('[data-wishlist-strings]').textContent);
  const catalogMap = catalog instanceof Map ? catalog : new Map(catalog.map((p) => [p.product_gid, p]));
  const rows = new Map();
  let inputMode = 'unknown';
  let opener = null;
  let undoProduct = null;
  const notifications = wishlistNotice(root);
  let lastAnnouncement = '';
  let hasSnapshot = false;
  let transientKey = '', transientReady = false, transientTimer = null;
  let refreshing = null, refreshAgain = false;
  function refresh(revalidate = false) {
    // A return arriving during another read needs one later identity check.
    // Coalescing must not swallow a logout/account switch while that read settles.
    if (refreshing) { if (revalidate) refreshAgain = true; return refreshing; }
    refreshing = (async () => {
      do { refreshAgain = false; await store.refresh(); }
      while (refreshAgain && !signal.aborted);
    })().finally(() => { refreshing = null; });
    return refreshing;
  }
  const setText = (node, value) => {
    const text = String(value ?? '');
    if (node.textContent !== text) node.textContent = text;
  };
  function presentation(view) {
    // Product state is committed immediately. Only short-lived background copy
    // waits, so rapid saves never replace the account invitation with a status.
    const key = view.problem === 'NETWORK' ? 'network' : !view.problem &&
      (view.pendingCount ? 'pending' : view.syncStatus === 'loading' ? 'loading' : '');
    if (key !== transientKey) {
      document.defaultView.clearTimeout(transientTimer);
      transientKey = key; transientReady = false;
      transientTimer = key ? document.defaultView.setTimeout(() => {
        transientReady = true; render(store.view);
      }, 3500) : null;
    }
    return key && !transientReady ? { ...view, pendingCount: 0,
      problem: view.problem === 'NETWORK' ? null : view.problem, syncStatus: 'synced' } : view;
  }

  function paintFocus(element, suppress) {
    if (!element?.matches(ownControl)) return;
    if (suppress) element.setAttribute('data-myart-pointer-focus', '');
    else element.removeAttribute('data-myart-pointer-focus');
  }
  function focus(element) {
    if (!element?.isConnected) return;
    paintFocus(element, inputMode === 'pointer' || inputMode === 'dismissal');
    element.focus({ preventScroll: inputMode !== 'keyboard' });
  }
  function announce(message) {
    notifications.announce(message); lastAnnouncement = message;
  }
  function close(mode = inputMode) {
    inputMode = mode;
    if (!isPage && dialog.open) dialog.close();
  }
  function expanded(value) {
    if (!pageUrl) for (const button of document.querySelectorAll('[data-wishlist-open]')) button.setAttribute('aria-expanded', String(value));
  }
  dialog.addEventListener('close', () => {
    expanded(false);
    root.append(notice);
    root.append(status);
    overlayHooks.release?.();
    focus(opener?.isConnected ? opener : document.querySelector('[data-wishlist-open]:not([hidden])'));
  }, { signal });
  dialog.addEventListener('cancel', (event) => {
    event.preventDefault();
    close('dismissal');
  }, { signal });
  dialog.addEventListener('click', (event) => {
    if (event.target !== dialog) return;
    const bounds = dialog.getBoundingClientRect();
    if (event.clientX < bounds.left || event.clientX > bounds.right ||
        event.clientY < bounds.top || event.clientY > bounds.bottom) close('pointer');
  }, { signal });
  function open(trigger) {
    if (pageUrl) { document.defaultView.location.assign(pageUrl); return; }
    const other = [...document.querySelectorAll('dialog[open],[aria-modal="true"]')]
      .some((node) => node !== dialog && !node.closest('details:not([open]),[hidden],[aria-hidden="true"]') &&
        node.getClientRects().length && document.defaultView.getComputedStyle(node).visibility !== 'hidden');
    if (other || overlayHooks.canOpen?.() === false) { announce(strings.otherPanel); return; }
    if (lastAnnouncement === strings.otherPanel) announce('');
    opener = trigger;
    panelNotice.append(notice);
    panelNotice.append(status);
    if (!dialog.open) {
      overlayHooks.acquire?.();
      dialog.showModal();
      expanded(true);
      focus(root.querySelector('[data-wishlist-close]'));
    }
    void refresh();
  }
  function render(view) {
    // The store's first notification precedes its journal read. It is not an
    // empty wishlist and must not flash the empty/loading composition.
    if (!hasSnapshot && view.syncStatus === 'loading') return;
    hasSnapshot = true;
    const displayed = presentation(view);
    ingestButtons();
    const identity=root.dataset.wishlistIdentity;
    const unverified=identity==='unverified' || identity==='mismatch';
    const needsConnection=unverified || identity==='preview';
    // Session-only fallback must not simultaneously promise persistent storage.
    const guestCopy = root.querySelector('[data-wishlist-guest-copy]');
    if (guestCopy) guestCopy.hidden = needsConnection || mode !== 'guest' || !view.persistenceAvailable || view.problem === 'LOCAL_STORAGE' ||
      (isPage && (view.count === 0 || Boolean(view.problem && view.problem !== 'NETWORK')));
    // The page uses one inline invitation; exceptional states keep the existing live status.
    if (isPage) sync.hidden = Boolean(guestCopy && !guestCopy.hidden);
    const gridHeading = root.querySelector('[data-wishlist-grid-heading]');
    if (gridHeading) gridHeading.hidden = view.count === 0;
    setText(count, view.count);
    const headingCount=root.querySelector('[data-wishlist-heading-count]');
    if(headingCount)setText(headingCount, view.count);
    const openers = [...document.querySelectorAll('[data-wishlist-open]')];
    for (const button of openers) {
      button.setAttribute('aria-label', strings.open + ', ' + view.count);
      if (button.hasAttribute('data-wishlist-floating')) {
        button.toggleAttribute('data-wishlist-empty-count', view.count === 0);
        button.style.setProperty('--wishlist-count-digits', String(String(view.count).length));
      }
      button.hidden = !button.hasAttribute('data-wishlist-floating') && root.contains(button) && openers.some((item) => !root.contains(item));
      if (pageUrl) button.removeAttribute('aria-expanded');
      else button.setAttribute('aria-expanded', String(dialog.open));
      for (const badge of button.querySelectorAll('[data-wishlist-count]')) {
        setText(badge, view.count);
        if (badge.hasAttribute('data-wishlist-hide-empty')) badge.hidden = view.count === 0;
      }
    }
    for (const button of document.querySelectorAll('[data-myart-wishlist-product]')) {
      const id = button.dataset.myartWishlistProduct;
      if (!catalogMap.has(id)) continue;
      const selected = Boolean(view.items[id]?.present);
      button.setAttribute('aria-pressed', String(selected));
      button.disabled = !catalogMap.get(id).eligible;
      button.hidden = false;
      const productEntry=button.closest('[data-wishlist-pdp-entry]');
      if(productEntry)productEntry.hidden=!productEntry.hasAttribute('data-wishlist-pdp-mounted');
      const visibleLabel=button.querySelector('[data-wishlist-button-label]');
      if(visibleLabel)visibleLabel.textContent=selected?strings.savedShort:strings.saveCurrent;
      button.setAttribute('aria-label', (selected ? strings.unsave : strings.save) + ' ' + catalogMap.get(id).title);
    }
    const visible = Object.values(view.items).filter((item) => item.present);
    const focused = document.activeElement;
    const removedRow = focused?.closest?.('[data-wishlist-row]');
    let focusAfterRemoval = null;
    if (removedRow && !visible.some((item) => item.product_gid === removedRow.dataset.wishlistRow)) {
      const siblings = [...list.children];
      const index = siblings.indexOf(removedRow);
      focusAfterRemoval = siblings[index + 1]?.querySelector('[data-wishlist-remove]') ||
        siblings[index - 1]?.querySelector('[data-wishlist-remove]') || empty.querySelector('a');
    }
    for (const [id, row] of rows) {
      if (!visible.some((item) => item.product_gid === id)) { row.remove(); rows.delete(id); }
    }
    for (const item of visible) {
      const product = catalogMap.get(item.product_gid);
      if (!rows.has(item.product_gid)) {
        const row = document.createElement('li');
        row.className = 'myart-wishlist-row';
        row.dataset.wishlistRow = item.product_gid;
        const link = document.createElement('a');
        link.className = 'myart-wishlist-row__link';
        link.dataset.wishlistCardLink = '';
        const media = document.createElement('div');
        media.className = 'myart-wishlist-row__media';
        const image = document.createElement('img');
        image.className = 'myart-wishlist-row__image';
        image.alt = ''; image.width = 720; image.height = 720;
        image.loading = rows.size < 4 ? 'eager' : 'lazy'; image.decoding = 'async';
        const sale = document.createElement('span'); sale.className='myart-wishlist-row__sale'; sale.hidden=true;
        media.append(image,sale);
        const title = document.createElement('h3');
        title.className = 'myart-wishlist-row__title';
        const hobby = document.createElement('span');
        hobby.className = 'myart-wishlist-row__hobby'; hobby.hidden=true;
        const reviews = document.createElement('div'); reviews.className='myart-wishlist-row__reviews'; reviews.hidden=true;
        const details = document.createElement('p'); details.className='myart-wishlist-row__details'; details.hidden=true;
        const price = document.createElement('p'); price.className='myart-wishlist-row__price';
        const compare=document.createElement('s'); compare.hidden=true;
        const amount=document.createElement('span'); amount.dataset.wishlistPrice='';
        price.append(compare,amount);
        const imageLink = document.createElement('a');
        imageLink.className = 'myart-wishlist-row__image-link';
        imageLink.dataset.wishlistCardLink = '';
        imageLink.tabIndex = -1;
        imageLink.setAttribute('aria-hidden', 'true');
        imageLink.append(...media.childNodes);
        media.append(imageLink);
        link.append(title,hobby,reviews,price,details);
        const remove = document.createElement('button');
        remove.type = 'button'; remove.className = 'myart-wishlist-toggle myart-wishlist-row__remove';
        remove.dataset.wishlistRemove = item.product_gid;
        remove.setAttribute('aria-pressed', 'true');
        const icon=heartIcon(document);
        remove.append(icon);
        row.append(media,link,remove);
        list.append(row);
        rows.set(item.product_gid, row);
      }
      rows.get(item.product_gid).classList.toggle('myart-wishlist-row--pending', Boolean(item.pending));
      const row = rows.get(item.product_gid);
      const title = row.querySelector('h3');
      const price = row.querySelector('.myart-wishlist-row__price');
      const link = row.querySelector('.myart-wishlist-row__link');
      const image = row.querySelector('img');
      const metadataPending = !product?.title && !product?.unavailable && !product?.loadError;
      row.toggleAttribute('data-wishlist-metadata-loading', metadataPending);
      row.setAttribute('aria-busy', String(metadataPending));
      setText(title, product?.title || (metadataPending ? '' : strings.savedItem));
      price.hidden = Boolean(product?.unavailable || (product?.loadError && !product?.priceLabel));
      setText(price.querySelector('[data-wishlist-price]'), product?.priceLabel || '');
      const details=row.querySelector('.myart-wishlist-row__details');
      details.hidden=!(product?.unavailable || product?.loadError);
      details.textContent=product?.unavailable ? strings.unavailable : strings.detailsError;
      const compare=price.querySelector('s');compare.hidden=!product?.comparePriceLabel;compare.textContent=product?.comparePriceLabel || '';
      const sale=row.querySelector('.myart-wishlist-row__sale');sale.hidden=!product?.saleLabel;sale.textContent=product?.saleLabel || '';
      const hobby=row.querySelector('.myart-wishlist-row__hobby');
      hobby.hidden=!product?.hobby;setText(hobby,product?.hobby || '');
      hobby.dataset.hobby=product?.hobbyKey || 'other';
      const reviews=row.querySelector('.myart-wishlist-row__reviews');
      const reviewKey=JSON.stringify([product?.rating,product?.reviewCount,product?.ratingLabel,product?.reviewLabel]);
      if(reviews.dataset.key!==reviewKey){reviews.dataset.key=reviewKey;reviews.replaceChildren();
        reviews.hidden=!(product?.rating>0 && product?.reviewCount>0);
        if(!reviews.hidden)renderRating(reviews,product,document);
      }
      const href = product?.eligible && safeUrl(product.url, document);
      for (const productLink of row.querySelectorAll('[data-wishlist-card-link]')) {
        if (href) { productLink.href = href; productLink.removeAttribute('aria-disabled'); }
        else { productLink.removeAttribute('href'); productLink.setAttribute('aria-disabled', 'true'); }
      }
      let add = row.querySelector('.myart-wishlist-row__add');
      const linkOnly = product?.productPageOnly === true;
      if (product?.handle && href && (!add || (add.tagName === 'A') !== linkOnly)) {
        add?.remove();
        add = document.createElement(linkOnly ? 'a' : 'button');
        add.className = 'myart-wishlist-row__add';
        if (!linkOnly) { add.type = 'button'; add.dataset.wishlistCartAdd = ''; }
        const label = document.createElement('span');
        add.append(label); row.append(add);
      }
      if (add) {
        add.hidden = !href || Boolean(product?.unavailable || product?.loadError);
        const label = linkOnly ? strings.customize : product?.needsOptions ? strings.chooseOptions : strings.addToCart;
        setText(add.querySelector('span'), label);
        add.setAttribute('aria-label', label + ' ' + (product?.title || ''));
        if (linkOnly) add.href = href;
        else {
          add.disabled = product?.available !== true;
          add.dataset.productHandle = product?.handle || '';
          add.dataset.variantId = product?.variantId || '';
          add.dataset.needsOptions = String(product?.needsOptions === true);
        }
      }
      const imageUrl = product?.image && safeUrl(product.image, document, true);
      image.hidden = !imageUrl;
      if (imageUrl && image.getAttribute('src') !== imageUrl) image.src = imageUrl;
      row.querySelector('[data-wishlist-remove]').setAttribute('aria-label', strings.unsave + ' ' + (product?.title || strings.unavailable));
    }
    empty.hidden = visible.length > 0;
    list.hidden = visible.length === 0;
    if (isPage) {
      const isEmpty = visible.length === 0;
      const loading = isEmpty && displayed.syncStatus === 'loading';
      const reviewState = root.dataset.wishlistReview;
      const hasReview = Boolean(reviewState);
      const accountError = isEmpty && (unverified || (mode === 'account' && displayed.problem === 'NETWORK'));
      root.dataset.wishlistPresentation = isEmpty ? (loading ? 'loading' : 'empty') : 'populated';
      const heading = root.querySelector('.myart-wishlist-page__heading');
      heading.hidden = isEmpty;
      dialog.setAttribute('aria-labelledby', isEmpty ? 'wishlist-empty-title' : 'wishlist-page-title');
      const title = empty.querySelector('[data-wishlist-empty-title]');
      setText(title, loading ? strings.emptyLoading : accountError ? strings.emptyNetworkTitle : hasReview ? strings.emptyReviewTitle : strings.emptyTitle);
      empty.querySelector('[data-wishlist-empty-description]').hidden = loading || accountError || hasReview;
      empty.querySelector('[data-wishlist-empty-account-prompt]').hidden = needsConnection || mode !== 'guest' || loading;
      empty.querySelector('[data-wishlist-empty-browse]').hidden = loading;
      const footer = root.querySelector('.myart-wishlist-page__sync');
      const slot = empty.querySelector('[data-wishlist-empty-status]');
      if (isEmpty && footer.parentElement !== slot) slot.append(footer);
      else if (!isEmpty && footer.parentElement === slot) gridHeading.before(footer);
      const reviews=root.querySelector('[data-wishlist-reviews]');
      if(isEmpty && reviews && reviews.parentElement!==slot)slot.append(reviews);
      else if(!isEmpty && reviews?.parentElement===slot)list.after(reviews);
      if(isEmpty && (accountError || hasReview || !view.persistenceAvailable))empty.querySelector('[data-wishlist-empty-browse]').before(slot);
      else if(isEmpty)empty.append(slot);
      const exceptional = Boolean(hasReview || displayed.problem || (mode === 'account' && displayed.pendingCount) || !view.persistenceAvailable);
      if (isEmpty) sync.hidden = loading || !exceptional || (mode === 'guest' && view.problem === 'NETWORK' && view.persistenceAvailable);
      if(hasReview)sync.hidden=false;
      footer.hidden = isEmpty && sync.hidden;
    }
    const syncText = sync.querySelector('[data-wishlist-sync-text]') || sync;
    const accountNetwork = isPage && mode === 'account' && displayed.problem === 'NETWORK' && view.persistenceAvailable;
    setText(syncText, accountNetwork ? (view.count ? strings.accountNetwork : strings.emptyNetwork) : wishlistSyncText(displayed, mode, strings));
    if(isPage && root.dataset.wishlistReview) syncText.textContent=root.dataset.wishlistReview==='pending'?strings.mergePending:strings.mergeReview;
    const retry = root.querySelector('[data-wishlist-retry]');
    if (retry === focused && view.problem !== 'NETWORK') focus(focusTarget());
    retry.hidden = displayed.problem !== 'NETWORK' || (isPage && mode === 'guest' && view.persistenceAvailable);
    if (isPage) {
      const emptyAccountNetwork = accountNetwork && !view.count;
      retry.textContent = emptyAccountNetwork ? strings.emptyNetworkRetry : strings.retry;
      let suffix = sync.querySelector('[data-wishlist-retry-suffix]');
      if (!suffix) {
        suffix = document.createElement('span');
        suffix.dataset.wishlistRetrySuffix = '';
        retry.after(suffix);
      }
      suffix.textContent = emptyAccountNetwork ? ' ' + strings.emptyNetworkAfterRetry : '';
    }
    const connect=sync.querySelector('[data-wishlist-connect]');
    if(connect)connect.hidden=!(identity==='preview' || identity==='mismatch');
    if(needsConnection){
      sync.hidden=false;
      const footer=root.querySelector('.myart-wishlist-page__sync');if(footer)footer.hidden=false;
      syncText.textContent=(identity==='preview'?strings.previewConnect:identity==='mismatch'?strings.accountReconnect:strings.identityUnavailable)+' ';
      retry.hidden=!unverified || identity==='mismatch';retry.textContent=strings.retry;
      const suffix=sync.querySelector('[data-wishlist-retry-suffix]');if(suffix)suffix.textContent='';
    }
    if (focusAfterRemoval) focus(focusAfterRemoval);
  }
  document.addEventListener('pointerdown', (event) => {
    const element = event.target.closest?.(ownControl);
    if (element) { inputMode = 'pointer'; paintFocus(element, true); }
  }, { signal, capture: true });
  document.addEventListener('keydown', (event) => {
    if (!navigationKeys.has(event.key)) return;
    inputMode = 'keyboard';
    for (const element of document.querySelectorAll(ownControl)) paintFocus(element, false);
  }, { signal, capture: true });
  document.addEventListener('focusout', (event) => {
    if (event.target.matches?.(ownControl)) event.target.removeAttribute('data-myart-pointer-focus');
  }, { signal });
  document.addEventListener('click', (event) => {
    const button = event.target.closest?.(ownControl);
    if (!button) return;
    if (event.detail === 0 && inputMode !== 'keyboard') inputMode = 'unknown';
    if (button.matches('[data-wishlist-open]')) {
      if (pageUrl && button.matches('a[href]')) return;
      open(button); return;
    }
    if (button.matches('[data-wishlist-close]')) { close(); return; }
    if (button.matches('[data-wishlist-retry]')) {
      if(retryIdentity && (['unverified','mismatch'].includes(root.dataset.wishlistIdentity) || root.dataset.wishlistMergePending))retryIdentity();
      else void refresh();return;
    }
    let id;
    let present;
    if (button.matches('[data-myart-wishlist-product]')) {
      id = button.dataset.myartWishlistProduct;
      if (!catalogMap.get(id)?.eligible) return;
      const latest = [...store.localIntents].reverse().find(intent=>intent.product_gid===id);
      present = !(latest ? latest.present : store.view.items[id]?.present);
    } else if (button.matches('[data-wishlist-remove]')) {
      id = button.dataset.wishlistRemove; present = false;
    } else if (button === undo && undoProduct) {
      id = undoProduct; present = true; undoProduct = null; undo.hidden = true;
    }
    if (!id) return;
    store.setItem(id, present);
    if (!present && isPage && root.contains(button)) {
      undoProduct = id; undo.hidden = false;
      notifications.show(strings.removed, () => { undoProduct = null; });
    } else {
      notifications.dismiss(); undoProduct = null;
    }
    if (button === undo) {
      focus((isPage || dialog.open) ? rows.get(id)?.querySelector('[data-wishlist-remove]') :
        [...document.querySelectorAll('[data-myart-wishlist-product]')].find((control) => control.dataset.myartWishlistProduct === id));
    }
    announce(present ? strings.added : strings.removed);
  }, { signal });
  document.defaultView.addEventListener('online', () => { void refresh(); }, { signal });
  document.defaultView.addEventListener('pageshow', () => { void refresh(); }, { signal });
  if (!journal && persistence.key) document.defaultView.addEventListener('storage', (event) => {
    if (event.key === persistence.key) void refresh();
  }, { signal });
  document.addEventListener('shopify:section:load', () => render(store.view), { signal });
  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target.contains(root)) instance.destroy();
  }, { signal });
  const unsubscribe = store.subscribe(render);
  const instance = {
    root, store, refresh,
    focusClose: () => focus(focusTarget()),
    updateCatalog() { render(store.view); },
    destroy() {
      close();
      (sync.querySelector('[data-wishlist-sync-text]') || sync).textContent = strings.loading;
      document.defaultView.clearTimeout(transientTimer);
      controller.abort();
      unsubscribe();
      const pending=store.destroy();
      for (const row of rows.values()) row.remove();
      rows.clear();
      for (const button of document.querySelectorAll('[data-wishlist-open],[data-myart-wishlist-product]')) button.hidden = true;
      for(const entry of document.querySelectorAll('[data-wishlist-pdp-entry]'))entry.hidden=true;
      notice.hidden = true;
      undo.hidden = true;
      overlayHooks.release?.();
      instances.delete(document);
      return pending;
    }
  };
  instances.set(document, instance);
  void refresh();
  return instance;
}
function decorateHeart(button) {
  if (!button.querySelector('svg')) button.append(heartIcon(button.ownerDocument));
}
return { wishlistSyncText, bootWishlist, decorateHeart, wishlistNotice };
})();
const wishlist_catalog = (() => {
const productId = /^gid:\/\/shopify\/Product\/[1-9][0-9]*$/;

const handleId = /^[a-z0-9][a-z0-9_-]{0,200}$/;

class WishlistCatalog {

  constructor({ document, rootUrl, currency, locale, fetcher = (...args) => fetch(...args) }) {

    if (!/^\/(?:[A-Za-z0-9-]+\/)*$/.test(rootUrl) || !/^[A-Z]{3}$/.test(currency)) throw Error('INVALID_CATALOG_CONFIG');

    this.document=document; this.rootUrl=rootUrl; this.fetcher=fetcher; this.items=new Map(); this.pending=new Map();

    this.format=new Intl.NumberFormat(locale,{style:'currency',currency}); this.changed=()=>{};

  }

  ingestButtons() {

    for (const button of this.document.querySelectorAll('[data-myart-wishlist-product]')) {

      const id=button.dataset.myartWishlistProduct; const handle=button.dataset.wishlistHandle;

      if (!productId.test(id) || !handleId.test(handle || '') || this.items.has(id)) continue;

      this.items.set(id,{product_gid:id,handle,title:button.dataset.wishlistTitle || '',url:button.dataset.wishlistUrl,

        image:button.dataset.wishlistImage,eligible:true});

    }

  }

  handle(id) { return this.items.get(id)?.handle || null; }

  async hydrate(references, signal) {

    if (!Array.isArray(references) || references.length>4000) throw Error('INVALID_CATALOG_STATE');

    for (let index=0; index<references.length; index+=4) {

      if (signal?.aborted) return;

      await Promise.all(references.slice(index,index+4).map((item)=>this.load(item,signal)));

    }

  }

  async load(item, signal) {

    if (!productId.test(item?.product_gid || '')) return;

    const known=this.items.get(item.product_gid); const handle=item.handle || known?.handle;

    if(known?.checkedAt && known.handle===handle && Date.now()-known.checkedAt<60000)return;

    if (!handleId.test(handle || '')) { this.items.set(item.product_gid,{...known,unavailable:true,eligible:false}); this.changed(); return; }

    if (this.pending.has(item.product_gid)) return this.pending.get(item.product_gid);

    const promise=(async()=>{

      let settled=false;

      const controller=new AbortController();const timeout=setTimeout(()=>controller.abort(),6000);

      const abort=()=>controller.abort();signal?.addEventListener('abort',abort,{once:true});

      try {

        if(signal?.aborted)return;

        const response=await this.fetcher(this.rootUrl+'products/'+encodeURIComponent(handle)+'?view=myart-wishlist-card',{

          // Shopify negotiates application/json before applying the Liquid view.

          credentials:'same-origin',headers:{Accept:'text/html'},redirect:'error',

          signal:controller.signal

        });

        if (response.status===404) { settled=true; this.items.set(item.product_gid,{...known,handle,unavailable:true,eligible:false}); return; }

        if (!response.ok) return;

        if(!/^(?:application\/json|text\/html)(?:\s*;|$)/i.test(response.headers.get('content-type') || ''))return;

        const reader=response.body?.getReader();let raw;

        if(reader){

          let size=0;const chunks=[];

          while(true){const {done,value}=await reader.read();if(done)break;size+=value.length;

            if(size>1048576){await reader.cancel();return;}chunks.push(value);}

          const bytes=new Uint8Array(size);let offset=0;for(const chunk of chunks){bytes.set(chunk,offset);offset+=chunk.length;}

          raw=JSON.parse(new TextDecoder().decode(bytes));

        }else{const text=await response.text();if(text.length>1048576)return;raw=JSON.parse(text);}

        if(Number.isSafeInteger(raw.id) && ('gid://shopify/Product/'+raw.id!==item.product_gid || raw.handle!==handle)){

          settled=true;this.items.set(item.product_gid,{...known,handle,unavailable:true,eligible:false});return;

        }

        if (!Number.isSafeInteger(raw.id) ||

            typeof raw.title!=='string' || !Number.isSafeInteger(raw.price) || raw.price<0 || raw.price>100000000 || raw.schema!==1 || typeof raw.priceLabel!=='string') return;

        settled=true;

        this.items.set(item.product_gid,{product_gid:item.product_gid,handle,title:raw.title,url:this.rootUrl+'products/'+handle,

          image:typeof raw.featured_image==='string'?raw.featured_image:null,

          eligible:true,available:raw.available===true,priceLabel:raw.priceLabel, comparePriceLabel:typeof raw.comparePriceLabel==='string'?raw.comparePriceLabel:null,

          saleLabel:typeof raw.saleLabel==='string'?raw.saleLabel:null,
          hobby:typeof raw.hobby?.label==='string'&&raw.hobby.label.length<=80?raw.hobby.label:null,
          hobbyKey:['pbn','punch','crochet','diamond','bags','gift','accessories'].includes(raw.hobby?.key)?raw.hobby.key:'other',
          variantId:Number.isSafeInteger(raw.variantId)&&raw.variantId>0?String(raw.variantId):null,
          needsOptions:raw.needsOptions===true,productPageOnly:raw.productPageOnly===true,

          rating:Number.isFinite(raw.rating)?Math.max(0,Math.min(5,raw.rating)):0,

          reviewCount:Number.isSafeInteger(raw.reviewCount)&&raw.reviewCount>0?raw.reviewCount:0,

          ratingLabel:typeof raw.ratingLabel==='string'?raw.ratingLabel:'',reviewLabel:typeof raw.reviewLabel==='string'?raw.reviewLabel:'',checkedAt:Date.now()});

      } catch { /* Keep known public metadata; network failure never removes a saved item. */ }

      finally {

        if(!settled && !signal?.aborted)this.items.set(item.product_gid,{...known,product_gid:item.product_gid,handle,url:this.rootUrl+'products/'+handle,eligible:true,loadError:true});

        clearTimeout(timeout);signal?.removeEventListener('abort',abort);this.changed(); }

    })().finally(()=>this.pending.delete(item.product_gid));

    this.pending.set(item.product_gid,promise);return promise;

  }

}




return { WishlistCatalog };
})();
const wishlist_api = (() => {
const { WishlistError, validateSnapshot } = wishlist_protocol;



class WishlistAPI {

  constructor({ apiPath, credential, generation, signal, fetcher = (...args) => fetch(...args), onIdentityChange = () => {} }) {

    if (!/^\/apps\/[a-z0-9-]+(?:\/[a-z0-9-]+)*$/.test(apiPath) || apiPath.length > 160 || apiPath.includes('//')) throw Error('INVALID_API_PATH');

    this.path=apiPath; this.credential=credential; this.generation=generation; this.signal=signal;

    this.fetcher=fetcher; this.onIdentityChange=onIdentityChange; this.proof=null; this.proofAt=0; this.refreshing=null;

    this.imported=new Set();

  }

  rememberImports(result, replace=false) {

    const ids=result.review_product_gids ?? result.unresolved_product_gids;

    if(ids===undefined)return;

    if(!Array.isArray(ids) || ids.length>4000 || ids.some(id=>!/^gid:\/\/shopify\/Product\/[1-9]\d*$/.test(id)))throw new WishlistError('INVALID_STATE');

    if(replace || result.review_product_gids)this.imported=new Set(ids);

    else for(const id of ids)this.imported.add(id);

  }

  savedState(state) {

    validateSnapshot(state);

    const result=structuredClone(state);

    if(this.proof?.kind!=='customer')return result;

    // Imported references are already durable, account-owned records returned

    // by the signed service. Present them in the ordinary list while upgrading

    // eligible records through the same versioned queue as other saves.

    for(const id of this.imported)if(!result.items[id]?.present){

      const old=result.items[id];result.items[id]={product_gid:id,present:true,

        preferred_variant_gid:old?.preferred_variant_gid || null,item_version:old?.item_version || 0,saved_revision:old?.saved_revision || 0};

    }

    return result;

  }

  async raw(action, fields = {}) {

    const timer=new AbortController(); const timeout=setTimeout(()=>timer.abort(),12000);

    const abort=()=>timer.abort(); this.signal?.addEventListener('abort',abort,{once:true});

    try {

      if (this.signal?.aborted) throw Error('ABORTED');

      const response=await this.fetcher(this.path+'/'+action,{method:'POST',credentials:'same-origin',cache:'no-store',redirect:'error',

        headers:{'Content-Type':'application/json',Accept:'application/json'},signal:timer.signal,

        body:JSON.stringify({guest_credential:this.credential,client_identity_generation:this.generation,

          ...(action!=='bootstrap'?{csrf_challenge:this.proof?.csrf_challenge,expected_scope:this.proof?.scope}:{}),...fields})});

      const text=await response.text(); if (text.length>1048576) throw Error('RESPONSE_LIMIT');

      let result; try { result=JSON.parse(text); } catch { throw Error('INVALID_RESPONSE'); }

      if (this.signal?.aborted) throw Error('ABORTED');

      if (!response.ok) {

        const error=new WishlistError(typeof result.error==='string'?result.error:'NETWORK',result.state);

        error.status=response.status; throw error;

      }

      if (result.client_identity_generation!==this.generation) throw new WishlistError('IDENTITY_CHANGED');

      return result;

    } finally { clearTimeout(timeout); this.signal?.removeEventListener('abort',abort); }

  }

  async bootstrap(initialize=false) {

    const result=await this.raw('bootstrap',{initialize});

    validateSnapshot(result.state);

    if (!['guest','customer'].includes(result.kind) || (result.scope!==null && !/^[a-f0-9-]{36}$/.test(result.scope || '')) ||

        (result.kind==='customer' && !result.scope) || typeof result.csrf_challenge!=='string') throw new WishlistError('INVALID_STATE');

    if (this.proof && (this.proof.kind!==result.kind || (this.proof.scope && this.proof.scope!==result.scope))) {

      this.onIdentityChange(); throw new WishlistError('IDENTITY_CHANGED');

    }

    const recovered=!this.proof && this.onProofRecovered;

    this.proof=result; this.proofAt=Date.now();

    this.rememberImports(result,true);

    if(recovered){this.onProofRecovered=null;recovered();throw new WishlistError('IDENTITY_CHANGED');}

    return result;

  }

  async request(action,fields={},retry=true) {

    if (!this.proof) {

      // A failed initial connection must be recoverable by Retry. Until a

      // native proof exists, this UI owns guest intentions only. An account

      // discovered here must go through the bootstrap/merge lifecycle first.

      this.refreshing ||= this.bootstrap(false).then((fresh) => {

        if (fresh.kind === 'customer') {

          this.onIdentityChange();

          throw new WishlistError('IDENTITY_CHANGED');

        }

        return fresh;

      }).finally(() => { this.refreshing = null; });

      await this.refreshing;

    }

    if (!this.proof.scope || Date.now()-this.proofAt>12*60000) {

      this.refreshing ||= this.bootstrap(action!=='state').finally(()=>{this.refreshing=null;});

      const fresh=await this.refreshing;

      if (action==='state' && !fresh.scope) return fresh;

      if (action!=='state' && !this.proof.scope) await this.bootstrap(true);

    }

    try {

      const result=await this.raw(action,fields);

      if(this.proof.kind==='customer' && ['state','merge'].includes(action))this.rememberImports(result);

      if (result.csrf_challenge) { this.proof.csrf_challenge=result.csrf_challenge; this.proofAt=Date.now(); }

      return result;

    } catch (error) {

      if (error.code==='IDENTITY_CHANGED' || (this.proof.kind==='guest' && ['GUEST_CONSUMED','GUEST_EXPIRED'].includes(error.code))) {

        this.onIdentityChange(); throw error;

      }

      // A challenge can expire while the request is queued. A fresh proof may

      // retry only the identical opaque principal, never an A operation on B.

      if (error.status===403 && retry) { await this.bootstrap(false); return this.request(action,fields,false); }

      throw error;

    }

  }

  transport(catalog) {

    return {state:async()=> {

      const result=await this.request('state');

      if(this.proof.scope)void this.request('products').then((data)=>catalog.hydrate(data.products,this.signal)).catch(()=>{});

      return this.savedState(result.state);

    },set:async(_credential,operation,durableCatalogHint=null)=>{

      if(!operation.present && this.imported.has(operation.product_gid)){

        await this.request('review',{product_gid:operation.product_gid});

        this.imported.delete(operation.product_gid);

      }

      let result;

      const handle=typeof durableCatalogHint === 'string' && /^[a-z0-9][a-z0-9_-]{0,200}$/.test(durableCatalogHint) ? durableCatalogHint : catalog.handle(operation.product_gid);

      try {result=await this.request('set',{operation,catalog_hint:handle});}

      catch(error){

        const current=error.state?.items?.[operation.product_gid];

        if(error.code!=='CONFLICT' || !current || current.present!==operation.present ||

          (operation.present && current.preferred_variant_gid!==operation.preferred_variant_gid))throw error;

        // Another device has already achieved this exact intention. Its signed

        // current snapshot is authoritative; do not replay a stale mutation.

        result={operation_id:operation.operation_id,status:'no_change',revision:error.state.revision,item:current};

      }

      if(operation.present)this.imported.delete(operation.product_gid);

      if(operation.present)void catalog.load({product_gid:operation.product_gid,handle},this.signal);

      return result;

    }};

  }

}




return { WishlistAPI };
})();
const wishlist_floating = (() => {
// Document-owned responsive access. Existing fixed purchase/privacy/preview bars
// keep their geometry; the wishlist moves above them and yields to open dialogs.
function mountWishlistFloating(root, signal) {
  const button = root.querySelector('[data-wishlist-floating]');
  if (!button && !root.hasAttribute('data-wishlist-page')) return;
  const doc = root.ownerDocument;
  const win = doc.defaultView;
  let frame = 0;
  function measure() {
    frame = 0;
    if (signal.aborted || !root.isConnected) return;
    const ownDialog = root.querySelector('[data-wishlist-dialog]');
    const visible = (node) => {
      const style = win.getComputedStyle(node);
      return node.getClientRects().length && style.display !== 'none' && style.visibility !== 'hidden' &&
        !node.closest('[hidden],[aria-hidden="true"],details:not([open])');
    };
    const modal = [...doc.querySelectorAll('dialog[open],[aria-modal="true"],[role="dialog"]')]
      .some((node) => node !== ownDialog && visible(node));
    const height = win.visualViewport?.height || win.innerHeight;
    const width = doc.documentElement.clientWidth;
    // Footer country/language controls are normal flow, not bottom-edge bars.
    // Yield while they are on screen; keep the wishlist reachable elsewhere.
    const footerLocalization = [...doc.querySelectorAll('footer localization-form .localization-form__select')]
      .some((node) => {
        if (!visible(node)) return false;
        const rect = node.getBoundingClientRect();
        return rect.top < height && rect.bottom > 0 && rect.left < width && rect.right > 0;
      });
    const localizationOpen = [...doc.querySelectorAll('localization-form .disclosure__list-wrapper:not([hidden])')]
      .some(visible);
    button?.toggleAttribute('data-wishlist-obscured', modal || footerLocalization || localizationOpen);
    let offset = 0;
    // Inspect only painted bottom-edge obstacles; no whole-document layout scan.
    const candidates = new Set();
    for (const x of [Math.max(1,width-24), Math.max(1,width-100)]) {
      for (const node of doc.elementsFromPoint(x, Math.max(1,height-4))) {
        for (let parent=node; parent && parent!==doc.body; parent=parent.parentElement) {
          if (!root.contains(parent)) candidates.add(parent);
        }
      }
    }
    for (const node of candidates) {
      const style=win.getComputedStyle(node);
      if (!['fixed','sticky'].includes(style.position) || !visible(node)) continue;
      const rect=node.getBoundingClientRect();
      if (rect.top > height*.5 && rect.bottom >= height-8 && rect.right > width-180) {
        offset=Math.max(offset, height-rect.top+8);
      }
    }
    root.style.setProperty('--wishlist-bottom-offset', Math.ceil(offset)+'px');
  }
  const schedule=()=>{if (!frame && !signal.aborted) frame=win.requestAnimationFrame(measure);};
  win.addEventListener('resize',schedule,{passive:true,signal});
  win.addEventListener('scroll',schedule,{passive:true,signal});
  win.visualViewport?.addEventListener('resize',schedule,{passive:true,signal});
  doc.addEventListener('click',schedule,{passive:true,signal});
  const observer=new MutationObserver((changes)=>{
    if (changes.some(change=>!root.contains(change.target))) schedule();
  });
  observer.observe(doc.body,{subtree:true,childList:true,attributes:true,attributeFilter:['class','open','hidden','aria-hidden','style']});
  // Own notice/font/wrapping changes can resize the floating hit area after
  // the click frame. Visibility:hidden preserves geometry, avoiding toggles.
  const resizeObserver = button && typeof win.ResizeObserver === 'function' ? new win.ResizeObserver(schedule) : null;
  if (resizeObserver) resizeObserver.observe(button);
  signal.addEventListener('abort',()=>{observer.disconnect();resizeObserver?.disconnect();win.cancelAnimationFrame(frame);},{once:true});
  schedule();
}

return { mountWishlistFloating };
})();
const wishlist_pdp = (() => {
/** One product action follows the first real picture, outside gallery gestures. */
function mountWishlistPdp(document, signal) {
  const entry = document.querySelector('[data-wishlist-pdp-entry]');
  if (!entry || signal.aborted) return () => false;
  const view = document.defaultView;
  const desktop = view.matchMedia('(min-width: 990px)');
  const home = document.createComment('wishlist product action staging');
  entry.before(home);
  let host = null, image = null, clip = null, hostEvents = null;
  let frame = 0, motionUntil = 0;
  const visible = (node) => node && node.getClientRects().length && view.getComputedStyle(node).visibility !== 'hidden';
  const resize = new view.ResizeObserver(() => schedule());
  const mutations = new view.MutationObserver(changes => {
    if (changes.some(change => change.target !== entry && !entry.contains(change.target))) followMotion();
  });
  function schedule() {
    if (!frame && !signal.aborted) frame = view.requestAnimationFrame(() => {
      frame = 0; reconcile();
      if (view.performance.now() < motionUntil) schedule();
    });
  }
  function followMotion() { motionUntil = view.performance.now() + 550; schedule(); }
  function releaseHost() {
    resize.disconnect(); mutations.disconnect(); hostEvents?.abort();
    host?.removeAttribute('data-wishlist-media-host');
    host = image = clip = hostEvents = null;
  }
  function reconcile() {
    if (signal.aborted) return false;
    const main = document.querySelector('#MainContent');
    const title = [...(main?.querySelectorAll('.productcard__info h1,.diamondcard__info h1,.product__title h1,.gift-card-hero__content h1') || [])].find(visible);
    const gallery = main?.querySelector('.productcard__media,.diamondcard__media');
    const rail = gallery?.querySelector('.productcard__item__zoom__slider,.diamondcard__item__zoom__slider');
    let nextHost = title, nextImage = null, nextClip = null;
    if (!desktop.matches && visible(rail)) {
      nextImage = [...rail.querySelectorAll('img.productcard__item__zoom__slider-image,img.diamondcard__item__zoom__slider-image')]
        .find(node => !node.closest('.owl-item.cloned') && visible(node));
      if (nextImage) { nextHost = gallery; nextClip = rail; }
    }
    if (!nextImage) {
      nextImage = [...(gallery?.querySelectorAll(':scope > img.productcard__item-image,:scope > img.diamondcard__item-image') || [])].find(visible);
      if (nextImage) nextHost = gallery;
    }
    if (!nextImage) {
      const media = [...(main?.querySelectorAll('media-gallery .product__media-item') || [])].find(node => visible(node.querySelector('.media-type-image .product__media img')));
      nextImage = media?.querySelector('.media-type-image .product__media img');
      if (nextImage) { nextHost = media; if (!desktop.matches) nextClip = media.closest('slider-component'); }
    }
    if (!nextImage) {
      // The app replaces Dawn's hidden gallery. Never place our action in its
      // clickable slides; observe their movement from the existing media owner.
      nextImage = [...(main?.querySelectorAll('.product__media-wrapper .pwzrswiper-slide img') || [])].find(visible);
      if (nextImage) {
        nextHost = nextImage.closest('.product__media-wrapper');
        if (!desktop.matches) nextClip = nextImage.closest('.pwzrswiper-container');
      }
    }
    if (!nextImage) {
      const gift = main?.querySelector('.gift-card-hero__stage');
      nextImage = gift?.querySelector('.gift-card-hero__art');
      if (nextImage && visible(gift)) nextHost = gift;
    }
    const mediaMode = nextImage && visible(nextImage) && nextHost;
    if (!mediaMode) { nextImage = nextClip = null; nextHost = title; }
    if (!nextHost) {
      releaseHost(); entry.hidden = true;
      delete entry.dataset.wishlistPdpMounted; delete entry.dataset.wishlistPdpClipped;
      if (home.isConnected && entry.previousSibling !== home) home.after(entry);
      return false;
    }
    const moved = host !== nextHost || image !== nextImage || clip !== nextClip || entry.parentElement !== nextHost;
    if (moved) {
      releaseHost(); host = nextHost; image = nextImage; clip = nextClip;
      if (mediaMode) host.dataset.wishlistMediaHost = '';
      host.append(entry); resize.observe(host); if (image) resize.observe(image);
      if (image) {
        hostEvents = new view.AbortController();
        for (const name of ['scroll','transitionrun','transitionend','animationstart','animationend']) {
          host.addEventListener(name, followMotion, { capture: true, passive: true, signal: hostEvents.signal });
        }
        mutations.observe(host, { childList: true, subtree: true, attributes: true, attributeFilter: ['class','style','hidden'] });
      }
    }
    const placement = mediaMode ? 'image' : 'title';
    if (entry.dataset.wishlistPdpMounted !== placement) entry.dataset.wishlistPdpMounted = placement;
    let clipped = false;
    if (image) {
      const rect = image.getBoundingClientRect(), parent = host.getBoundingClientRect();
      const size = desktop.matches ? 66 : 52;
      const top = Math.max(0, rect.top - parent.top - host.clientTop + host.scrollTop) + 6;
      const left = rect.right - parent.left - host.clientLeft + host.scrollLeft - size - 6;
      for (const [name, value] of [['--wishlist-image-top', top], ['--wishlist-image-left', left]]) {
        const px = `${Math.round(value * 100) / 100}px`;
        if (entry.style.getPropertyValue(name) !== px) entry.style.setProperty(name, px);
      }
      if (clip) {
        const bounds = clip.getBoundingClientRect(), x = rect.right - size - 6, y = rect.top + 6;
        clipped = x < bounds.left || x + size > bounds.right || y < bounds.top || y + size > bounds.bottom;
      }
    }
    entry.toggleAttribute('data-wishlist-pdp-clipped', clipped);
    entry.hidden = entry.querySelector('[data-myart-wishlist-product]')?.hidden !== false;
    return moved;
  }
  desktop.addEventListener('change', schedule, { signal });
  view.addEventListener('resize', schedule, { signal, passive: true });
  document.addEventListener('load', schedule, { signal, capture: true });
  document.fonts?.ready.then(schedule); reconcile();
  signal.addEventListener('abort', () => {
    view.cancelAnimationFrame(frame); motionUntil = 0; releaseHost(); entry.hidden = true;
    delete entry.dataset.wishlistPdpMounted; delete entry.dataset.wishlistPdpClipped;
    entry.style.removeProperty('--wishlist-image-top'); entry.style.removeProperty('--wishlist-image-left');
    if (home.isConnected) home.replaceWith(entry);
  }, { once: true });
  return reconcile;
}
return { mountWishlistPdp };
})();;
const wishlist_preview = (() => {
const { operationId } = wishlist_store;



// Shopify preview hosts cannot establish customer accounts. Only this store's

// preview may hand its public product references to its canonical storefront.

// No account IDs, guest credentials, cookies or account snapshots cross origins.

function wishlistLoginUrl(config, location) {

  const origin = new URL(config.storeUrl || location.origin).origin;

  const login = new URL(config.loginUrl || '/account/login', origin);

  login.protocol = new URL(origin).protocol; login.host = new URL(origin).host;

  const target = new URL(config.wishlistUrl || '/pages/wishlist', origin);

  if (config.previewTheme) target.searchParams.set('preview_theme_id', String(config.previewTheme));

  login.searchParams.set('return_to', target.pathname + target.search);

  return { origin, login:login.href, target:target.href };

}

function previewOrigin(value, shopId) {

  try {

    const url=new URL(value);

    return url.protocol==='https:' && !url.port && /^\d+$/.test(String(shopId)) &&

      new RegExp('^[a-z0-9]+-'+shopId+'\\.shopifypreview\\.com$').test(url.hostname);

  } catch { return false; }

}

function validatePreviewProducts(products) {

  if (!Array.isArray(products) || products.length>200) throw Error('INVALID_HANDOFF');

  const seen=new Set();

  for (const product of products) {

    if (!product || Object.keys(product).some(k=>!['product_gid','handle'].includes(k)) ||

        !/^gid:\/\/shopify\/Product\/[1-9]\d*$/.test(product.product_gid) ||

        typeof product.handle!=='string' || !/^[a-z0-9][a-z0-9_-]{0,254}$/.test(product.handle) ||

        seen.has(product.product_gid)) throw Error('INVALID_HANDOFF');

    seen.add(product.product_gid);

  }

  return products;

}

async function importPreviewProducts(journal, products) {

  validatePreviewProducts(products);

  if (!journal.available) throw Error('LOCAL_STORAGE');

  const result=await journal.transaction(draft=>{

    if (!draft || draft.merge_attempt) throw Error('PENDING_MERGE');

    const present=new Set(Object.values(draft.state.items).filter(i=>i.present).map(i=>i.product_gid));

    for(const op of draft.queue)if(op.present)present.add(op.product_gid);else present.delete(op.product_gid);

    if(new Set([...present,...products.map(p=>p.product_gid)]).size>200)throw Error('LIMIT');

    for(const product of products) {

      draft.catalog_hints ||= {};draft.catalog_hints[product.product_gid]=product.handle;

      if(present.has(product.product_gid))continue;

      draft.queue.push({operation_id:operationId(),product_gid:product.product_gid,present:true,

        preferred_variant_gid:null,expected_item_version:draft.state.items[product.product_gid]?.item_version || 0,

        created_at:Date.now(),sent:false});

    }

    return {record:draft,value:null};

  });

  if(!journal.available)throw Error('LOCAL_STORAGE');

  return result;

}

function receivePreviewWishlist({window,config,journal,signal}) {

  const hash=new URLSearchParams(window.location.hash.slice(1));

  const nonce=hash.get('wishlist-transfer'),source=hash.get('source');

  if(!nonce)return null;

  window.history.replaceState(null,'',window.location.pathname+window.location.search);

  const route=wishlistLoginUrl(config,window.location);

  if(window.location.origin!==route.origin || !window.opener || !/^[a-f0-9]{32}$/.test(nonce) || !previewOrigin(source,config.shopId))

    return Promise.reject(Error('INVALID_HANDOFF'));

  return new Promise((resolve,reject)=>{

    const opener=window.opener;let received=false;

    const finish=()=>{clearTimeout(timeout);window.removeEventListener('message',receive);signal.removeEventListener('abort',aborted);};

    const aborted=()=>{finish();reject(Error('ABORTED'));};

    const receive=async event=>{

      if(received || event.source!==opener || event.origin!==source || event.data?.type!=='myart-wishlist-transfer' || event.data.nonce!==nonce)return;

      received=true;

      try {

        await importPreviewProducts(journal,event.data.products);

        if(signal.aborted)throw Error('ABORTED');

        opener.postMessage({type:'myart-wishlist-received',nonce},source);

        finish();window.opener=null;resolve(true);

      } catch(error){finish();reject(error);}

    };

    const timeout=setTimeout(()=>{finish();reject(Error('HANDOFF_TIMEOUT'));},15000);

    signal.addEventListener('abort',aborted,{once:true});window.addEventListener('message',receive);

    opener.postMessage({type:'myart-wishlist-ready',nonce},source);

  });

}

function mountPreviewLogin({root,config,getProducts,notify,signal}) {

  const window=root.ownerDocument.defaultView,route=wishlistLoginUrl(config,window.location);

  for(const a of root.querySelectorAll('[data-wishlist-login]'))a.href=route.login;

  const preview=window.location.origin!==route.origin && previewOrigin(window.location.origin,config.shopId);

  if(!preview)return false;

  root.addEventListener('click',event=>{

    const link=event.target.closest('[data-wishlist-login]');if(!link)return;

    const products=getProducts();if(!products.length)return;

    event.preventDefault();

    try{validatePreviewProducts(products);}catch{notify('transferFailed');return;}

    const nonce=Array.from(window.crypto.getRandomValues(new Uint8Array(16)),b=>b.toString(16).padStart(2,'0')).join('');

    const target=new URL(route.target);target.hash=new URLSearchParams({'wishlist-transfer':nonce,source:window.location.origin}).toString();

    const child=window.open(target.href,'_blank');

    if(!child){notify('transferBlocked');return;}

    let sent=false;

    const finish=()=>{clearTimeout(timeout);window.removeEventListener('message',receive);signal.removeEventListener('abort',finish);};

    const receive=event=>{

      if(event.source!==child || event.origin!==route.origin || event.data?.nonce!==nonce)return;

      if(event.data.type==='myart-wishlist-ready' && !sent){sent=true;child.postMessage({type:'myart-wishlist-transfer',nonce,products},route.origin);}

      if(event.data.type==='myart-wishlist-received'){finish();notify('transferComplete');}

    };

    const timeout=setTimeout(()=>{finish();notify('transferFailed');},20000);

    window.addEventListener('message',receive);signal.addEventListener('abort',finish,{once:true});

  },{signal});

  return true;

}




return { wishlistLoginUrl, previewOrigin, validatePreviewProducts, importPreviewProducts, receivePreviewWishlist, mountPreviewLogin };
})();
const wishlist_bootstrap = (() => {
const { IndexedDBJournal } = wishlist_journal;
const { randomHex, operationId } = wishlist_store;
const { emptyState, snapshot, WishlistError } = wishlist_protocol;
const { bootWishlist, wishlistNotice } = wishlist_ui;
const { WishlistAPI } = wishlist_api;
const { WishlistCatalog } = wishlist_catalog;
const { mountWishlistFloating } = wishlist_floating;
const { mountWishlistPdp } = wishlist_pdp;
const { mountPreviewLogin, receivePreviewWishlist, wishlistLoginUrl } = wishlist_preview;

// Guest state is persistent. Account snapshots stay in memory; only pending
// operations persist under the opaque scope proved by native Shopify identity.
const memoryPersistence={available:true,read:()=>null,write:()=>{}};
const freshRecord=()=>({schema:1,credential:randomHex(),state:snapshot(emptyState()),queue:[],problem:null,catalog_hints:{}});
let active=null;
async function startWishlist(document=window.document) {
  if (active && !active.root.isConnected) active.destroy();
  if (active) return active;
  const root=document.querySelector('[data-myart-wishlist-root]');
  const settings=document.querySelector('[data-myart-wishlist-config]');
  if (!root || !settings) return;
  let config; try {config=JSON.parse(settings.textContent);} catch {return;}
  const lifetime=new AbortController();
  mountWishlistFloating(root,lifetime.signal);
  const reconcilePdp=mountWishlistPdp(document,lifetime.signal);
  const databaseName='myart-wishlist-'+config.apiPath.replace(/[^a-z0-9]/g,'-')+'-journal-v1';
  const journal=new IndexedDBJournal({databaseName});
  let accountJournal=null;
  let generation=0; let run=null; let instance=null; let api=null; let stopped=false; let connecting=null; let reconnect=false;
  let draining=Promise.resolve();
  let retryTimer=null;let transientAttempts=0;
  const strings=JSON.parse(root.querySelector('[data-wishlist-strings]').textContent);
  const catalog=new WishlistCatalog({document,...config});
  let transferStarted=false;
  const preview=mountPreviewLogin({root,config,signal:lifetime.signal,
    getProducts:()=>Object.values(instance?.store.view.items || {}).filter(item=>item.present)
      .map(item=>({product_gid:item.product_gid,handle:catalog.handle(item.product_gid)})),
    notify:key=>{const notifications=wishlistNotice(root);notifications.dismiss();
      notifications.announce(strings[key]);notifications.show(strings[key]);}});

  const reviews=root.querySelector('[data-wishlist-reviews]');
  const reviewRows=new Map();
  let reviewIds=[]; let mergeProblem=false;
  catalog.changed=()=>{instance?.updateCatalog();renderReviews();};
  function hide() {
    if(retryTimer!==null){document.defaultView.clearTimeout(retryTimer);retryTimer=null;}
    run?.abort(); run=null;
    draining=Promise.allSettled([draining,instance?.destroy()]);instance=null;api=null;
    if (accountJournal) {const closing=accountJournal;void draining.then(()=>closing.close());accountJournal=null;}
    reviews?.replaceChildren();reviewRows.clear(); if (reviews) reviews.hidden=true;
    root.dataset.wishlistMode='disabled';delete root.dataset.wishlistReview;
  }
  function renderReviews() {
    // Account-owned imported products use the ordinary product grid. There is
    // no second customer decision or manual Review/Save/Dismiss stage.
    if(reviews)reviews.hidden=true;
    delete root.dataset.wishlistReview;
  }
  async function queueImports(own,currentJournal,canonicalState,signal) {
    if(!own.imported.size)return;
    let refs;
    try {refs=(await own.request('products')).products;}catch{return;}
    if(signal.aborted)return;
    for(const product of refs || [])if(/^gid:\/\/shopify\/Product\/[1-9]\d*$/.test(product.product_gid) && /^[a-z0-9][a-z0-9_-]{0,200}$/.test(product.handle || '')){
      const known=catalog.items.get(product.product_gid);
      if(!known)catalog.items.set(product.product_gid,{product_gid:product.product_gid,handle:product.handle,eligible:true});
    }
    const slots=Math.max(0,200-Object.values(canonicalState.items).filter(item=>item.present).length);
    await currentJournal.transaction(draft=>{
      let remaining=slots;
      for(const id of own.imported){
        if(!remaining)break;
        if(canonicalState.items[id]?.present || draft.queue.some(op=>op.product_gid===id) || !catalog.handle(id))continue;
        draft.queue.push({operation_id:operationId(),product_gid:id,present:true,preferred_variant_gid:null,
          expected_item_version:canonicalState.items[id]?.item_version || 0,created_at:Date.now(),sent:false});
        draft.catalog_hints ||= {};draft.catalog_hints[id]=catalog.handle(id);remaining--;
      }
      return {record:draft,value:null};
    });
  }
  async function rotate(record) {
    return journal.transaction((draft)=>({record:draft?.credential===record.credential?freshRecord():draft,value:null}));
  }
  async function mergeGuest(proof,record,own) {
    if (!Object.values(record.state.items).some((item)=>item.present) && !record.queue.length) return proof.state;
    if (record.merge_attempt?.destination_scope && record.merge_attempt.destination_scope!==proof.scope) {
      // A frozen import already belongs to A. B sees only B's native list.
      return proof.state;
    }
    if (record.queue.length>1000) {mergeProblem=true;return proof.state;}
    const frozen=await journal.transaction((draft)=>{
      if (draft.credential!==record.credential) return {record:draft,value:null};
      draft.merge_attempt ||= {destination_scope:proof.scope,operation_id:operationId(),
        expected_source_revision:draft.state.revision,pending:draft.queue.map(({sent,created_at,...op})=>op)};
      return {record:draft,value:structuredClone(draft.merge_attempt)};
    });
    if (!frozen.value || frozen.value.destination_scope!==proof.scope) return proof.state;
    const attempt=frozen.value;
    const hints={...frozen.record.catalog_hints};
    for (const [id,product] of catalog.items) if (product.handle) hints[id]=product.handle;
    const body={merge:{operation_id:attempt.operation_id,expected_source_revision:attempt.expected_source_revision,pending:attempt.pending},
      catalog_hints:Object.fromEntries(Object.entries(hints).filter(([id])=>
        record.state.items[id]?.present || attempt.pending.some((op)=>op.product_gid===id)))};
    try {
      const result=await own.request('merge',body);
      if (own.signal.aborted) throw Error('ABORTED');
      reviewIds=result.unresolved_product_gids || []; await rotate(frozen.record); return result.state;
    } catch (error) {
      if (error.code==='GUEST_CONSUMED') {await rotate(frozen.record);return proof.state;}
      if (error.code==='MERGE_CONFLICT' && error.state) {
        await journal.transaction((draft)=>{
          if(draft.credential===record.credential){draft.state=error.state;draft.merge_attempt=null;}
          return {record:draft,value:null};
        });
        // The server rejected before commit. Retry from a new frozen snapshot;
        // per-item stale intentions are still held for explicit account review.
        reconnect=true;
      }
      // Keep the original import UUID/intents for a lost response. Never silently
      // rebase a conflicting offline addition over an account removal.
      mergeProblem=true;return proof.state;
    }
  }
  async function connect() {
    if (stopped || connecting) return connecting;
    const token=++generation;hide();mergeProblem=false;reviewIds=[];delete root.dataset.wishlistMergePending;
    delete root.dataset.wishlistStartupError;
    run=new AbortController();const ownSignal=run.signal;
    connecting=(async()=>{
      await draining;
      if(ownSignal.aborted || token!==generation)return;
      let {record}=await journal.transaction((draft)=>({record:draft || freshRecord(),value:null}));
      let transferred=false;
      if(!transferStarted){transferStarted=true;const incoming=receivePreviewWishlist({window:document.defaultView,config,journal,signal:lifetime.signal});
        if(incoming){transferred=await incoming;record=(await journal.read()).record;}}
      catalog.ingestButtons();
      for (const [id,handle] of Object.entries(record.catalog_hints || {})) {
        if (!catalog.items.has(id)) catalog.items.set(id,{product_gid:id,handle,eligible:true});
      }
      const own=new WishlistAPI({apiPath:config.apiPath,credential:record.credential,generation:token,signal:ownSignal,
        onIdentityChange:()=>{if(token===generation && !stopped){hide();reconnect=true;if(!connecting){reconnect=false;void connect();}}}});
      api=own;
      let proof;
      root.dataset.wishlistIdentity='verified';
      try {proof=await own.bootstrap(false);} catch(error) {
        if (['GUEST_CONSUMED','GUEST_EXPIRED'].includes(error.code)) {
          await rotate(record);hide();setTimeout(()=>{void connect();},0);return;
        }
        // Offline first saves remain guest intents. An unproved transport never
        // sends them as customer mutations. Reconnection first destroys this UI.
        if (ownSignal.aborted || token!==generation) return;
        proof={kind:'guest',scope:null,state:record.state};
        root.dataset.wishlistIdentity=preview?'preview':'unverified';
        own.onProofRecovered=()=>{if(token===generation && !stopped){hide();reconnect=true;if(!connecting){reconnect=false;void connect();}}};
      }
      if (ownSignal.aborted || token!==generation) return;
      if(proof.kind==='guest' && config.customerPresent && !preview && own.proof)root.dataset.wishlistIdentity='mismatch';
      if(transferred && proof.kind==='guest'){
        document.defaultView.location.assign(wishlistLoginUrl(config,document.defaultView.location).login);return;
      }
      let currentJournal=journal;let state=proof.state;const account=proof.kind==='customer';
      if (account) {
        reviewIds=proof.review_product_gids || [];
        state=await mergeGuest(proof,record,own);
        const canonicalState=state;state=own.savedState(state);
        if (ownSignal.aborted || token!==generation) return;
        accountJournal=new IndexedDBJournal({databaseName,recordKey:'account:'+proof.scope,ephemeralState:state});
        const ownJournal=accountJournal;
        await ownJournal.transaction((draft)=>({record:draft || {...freshRecord(),state},value:null}));
        await queueImports(own,ownJournal,canonicalState,ownSignal);
        if (ownSignal.aborted || token!==generation) return;
        currentJournal=ownJournal;
      }
      root.dataset.wishlistMode=account?'account':'guest';
      for (const element of root.querySelectorAll('[data-wishlist-login],[data-wishlist-guest-copy]')) element.hidden=account;
      root.querySelector('[data-wishlist-account-copy]').hidden=!account;
      const transport=own.transport(catalog);
      if(mergeProblem){root.dataset.wishlistMergePending='true';transport.state=async()=>{throw new WishlistError('NETWORK');};}
      instance=bootWishlist(root,{transport,persistence:memoryPersistence,journal:currentJournal,
        retryIdentity:()=>schedule(),
        catalog:catalog.items,mode:account?'account':'guest',catalogHint:(id)=>catalog.handle(id),ingestButtons:()=>catalog.ingestButtons(),
        overlayHooks:{acquire:()=>document.documentElement.classList.add('myart-wishlist-modal-open'),
          release:()=>document.documentElement.classList.remove('myart-wishlist-modal-open'),
          canOpen:()=>!document.body.classList.contains('myart-nav-is-open') && ![...document.querySelectorAll('details[open],cart-drawer.active,[data-myart-nav-state="open"]')]
          .some((node)=>node.getClientRects().length && (node.matches('cart-drawer.active,[data-myart-nav-state="open"]') ||
            node.querySelector('[aria-modal="true"],.menu-drawer,.search-modal')))}});
      instance.store.subscribe((view)=>{renderReviews();
        // Product presentation must not wait for a remote save acknowledgement.
        // This also restores prices/images for a locally queued save after reload.
        const visible=Object.values(view.items).filter((item)=>item.present);
        void catalog.hydrate(visible.map((item)=>({product_gid:item.product_gid,handle:instance.store.catalogHints?.[item.product_gid] || catalog.handle(item.product_gid)})),ownSignal);
        if(own.proof?.scope) void consent(own,token);});
      renderReviews();
      if (own.proof?.scope) {
        void consent(own,token);
        void own.request('products').then((data)=>{if(token===generation && !ownSignal.aborted)return catalog.hydrate(data.products,ownSignal);}).catch(()=>{});
      } else {
        const refs=Object.values(record.state.items).filter((item)=>item.present).map((item)=>({product_gid:item.product_gid,handle:record.catalog_hints?.[item.product_gid]}));
        void catalog.hydrate(refs,ownSignal);
      }
    })().catch(()=>{if(token===generation && !stopped){
      hide();root.dataset.wishlistStartupError='true';
      if(root.hasAttribute('data-wishlist-page')){
        root.dataset.wishlistPresentation='empty';
        root.querySelector('.myart-wishlist-page__heading').hidden=true;
        root.querySelector('[data-wishlist-grid-heading]').hidden=true;
        root.querySelector('[data-wishlist-list]').hidden=true;
        const empty=root.querySelector('[data-wishlist-empty]');empty.hidden=false;
        empty.querySelector('[data-wishlist-empty-title]').textContent=strings.startupErrorTitle;
        root.querySelector('[data-wishlist-dialog]').setAttribute('aria-labelledby','wishlist-empty-title');
        empty.querySelector('[data-wishlist-empty-description]').hidden=true;
        empty.querySelector('[data-wishlist-empty-account-prompt]').hidden=true;
        const footer=root.querySelector('.myart-wishlist-page__sync');footer.hidden=false;
        const slot=empty.querySelector('[data-wishlist-empty-status]');slot.append(footer);empty.querySelector('[data-wishlist-empty-browse]').before(slot);
        root.querySelector('[data-wishlist-sync]').hidden=false;
        root.querySelector('[data-wishlist-sync-text]').textContent=strings.startupError+' ';
        for(const node of root.querySelectorAll('[data-wishlist-guest-copy],[data-wishlist-account-copy],[data-wishlist-connect]'))node.hidden=true;
        const suffix=root.querySelector('[data-wishlist-retry-suffix]');if(suffix)suffix.textContent='';
        const retry=root.querySelector('[data-wishlist-retry]');retry.textContent=strings.retry;retry.hidden=false;
      }
    }}).finally(()=>{
      connecting=null;if(reconnect && !stopped && document.visibilityState!=='hidden'){reconnect=false;void connect();}
      else if(mergeProblem && !stopped && document.visibilityState!=='hidden' && transientAttempts<3){
        const delay=1000*2**transientAttempts++;retryTimer=document.defaultView.setTimeout(()=>{retryTimer=null;schedule();},delay);
      }else if(!mergeProblem)transientAttempts=0;
    });
    return connecting;
  }
  let lastConsent=null;
  let consentSerial=Promise.resolve();
  function consent(own,token) {
    // Serialize observations so a slow earlier grant cannot overtake a revoke.
    // Read privacy when dispatched, and refresh successful proof before its
    // server-side 24h lifetime ends, including a still-open page after sleep.
    const work=consentSerial.then(async()=>{
      if (token!==generation || !own.proof?.scope) return;
      const allowed=window.Shopify?.customerPrivacy?.marketingProcessingAllowed?.()===true;
      const key=own.proof.scope+':'+allowed;
      const now=Date.now();
      if(lastConsent?.key===key && now>=lastConsent.observedAt && now-lastConsent.observedAt<12*60*60*1000)return;
      try {
        await own.request('consent',{tracking_allowed:allowed});
        if(token===generation)lastConsent={key,observedAt:now};
      } catch { /* A failed observation stays eligible for the next refresh. */ }
    });
    consentSerial=work.catch(()=>{});
    return work;
  }
  const schedule=()=>{if(!stopped){generation++;hide();if(connecting)reconnect=true;else void connect();}};
  // A return to the tab is a refresh, not a new UI lifetime. The signed state
  // request still checks the current principal; onIdentityChange tears down the
  // old account immediately when the server reports a different identity.
  const resume=()=>{
    if(stopped || document.visibilityState==='hidden')return;
    if(instance && root.dataset.wishlistIdentity==='verified' && !root.dataset.wishlistMergePending){
      void instance.refresh(true);
    }else schedule();
  };
  root.addEventListener('click',event=>{if(root.dataset.wishlistStartupError && event.target.closest('[data-wishlist-retry]'))schedule();},{signal:lifetime.signal});
  window.addEventListener('online',resume,{signal:lifetime.signal});
  window.addEventListener('pageshow',(event)=>{if(event.persisted)resume();},{signal:lifetime.signal});
  document.addEventListener('visibilitychange',resume,{signal:lifetime.signal});
  document.addEventListener('visitorConsentCollected',()=>{if(api)void consent(api,generation);},{signal:lifetime.signal});
  const poll=root.hasAttribute('data-wishlist-page')?document.defaultView.setInterval(()=>{
    if(document.visibilityState==='visible' && !connecting && instance && !instance.store.running){
      if(root.dataset.wishlistMergePending)schedule();else void instance.refresh();
    }
  },15000):null;
  lifetime.signal.addEventListener('abort',()=>{if(poll!==null)document.defaultView.clearInterval(poll);},{once:true});
  document.addEventListener('shopify:section:load',()=>{reconcilePdp();catalog.ingestButtons();instance?.updateCatalog();},{signal:lifetime.signal});
  const observer=new MutationObserver((changes)=>{
    const movedPdp=reconcilePdp();
    if (movedPdp || changes.some((change)=>[...change.addedNodes].some((node)=>node.nodeType===1 && !root.contains(node) &&
      (node.matches('[data-myart-wishlist-product],[data-wishlist-open]') || node.querySelector('[data-myart-wishlist-product],[data-wishlist-open]'))))) {
      catalog.ingestButtons();instance?.updateCatalog();
    }
  });observer.observe(document.body,{childList:true,subtree:true});
  active={root,destroy(){if(stopped)return;stopped=true;generation++;hide();lifetime.abort();wishlistNotice(root).destroy();observer.disconnect();void journal.close();active=null;}};
  await connect();return active;
}
if (typeof window!=='undefined') {
  document.addEventListener('shopify:section:load',()=>{void startWishlist();});
  if (document.readyState==='loading')document.addEventListener('DOMContentLoaded',()=>{void startWishlist();},{once:true});
  else void startWishlist();
}
return { startWishlist };
})();
